import { arrayMove } from '@dnd-kit/sortable'
import { TASK_TRACKER_FILTER_FIELD_TYPES } from './taskTrackerFilter.constants'
import { isTaskTrackerSystemStickerFieldType } from './taskTrackerBoard.stickerFilters.utils'
import type { BoardStickerRow, TaskTrackerBoardSticker } from '../store/domain.types'
import type {
  TaskTrackerFilterFieldType,
  TaskTrackerFilterSelectOption,
  TaskTrackerFilter,
} from '../store/taskTracker.types'

const FILTER_FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Текст',
  date: 'Дата',
  datetime: 'Дата и время',
  number: 'Число',
  select: 'Список',
  checkbox: 'Флажок',
  user: 'Пользователь',
  performer: 'Исполнитель',
  deadline: 'Дедлайн',
  task_created_at: 'Дата создания',
  task_completed_at: 'Дата выполнения',
}

export const isTaskTrackerSystemFilter = (
  filter: Pick<TaskTrackerFilter, 'isSystem'>,
) => Boolean(filter.isSystem)

export const getTaskTrackerFilterFieldTypeLabel = (fieldType: string) =>
  FILTER_FIELD_TYPE_LABELS[fieldType] ?? fieldType

export const isTaskTrackerFilterColorValid = (color: string) =>
  /^#[0-9a-f]{6}$/i.test(color.trim())

export const readTaskTrackerFilterInputValue = (event: {
  target?: { value?: string }
  currentTarget?: { value?: string } | null
}) => event.target?.value ?? event.currentTarget?.value ?? ''

export const normalizeTaskTrackerFilterFieldType = (
  fieldType: string,
): TaskTrackerFilterFieldType => {
  if ((TASK_TRACKER_FILTER_FIELD_TYPES as string[]).includes(fieldType)) {
    return fieldType as TaskTrackerFilterFieldType
  }

  return 'text'
}

export const serializeTaskTrackerFilterOptions = (
  options: TaskTrackerFilterSelectOption[],
) =>
  JSON.stringify(
    options.map((option) => ({
      label: option.label.trim(),
      color: option.color.trim(),
    })),
  )

export const sortTaskTrackerFilters = (filters: TaskTrackerFilter[]) =>
  [...filters].sort(
    (a, b) => a.position - b.position || a.id - b.id,
  )

export const sortBoardStickerRows = (rows: BoardStickerRow[]) =>
  [...rows].sort(
    (left, right) =>
      left.boardPosition - right.boardPosition ||
      left.fieldSettingId - right.fieldSettingId,
  )

export const mapBoardStickerToRow = (
  sticker: TaskTrackerBoardSticker,
): BoardStickerRow => ({
  fieldSettingId: sticker.fieldSettingId,
  fieldType: sticker.fieldType,
  title: sticker.title,
  color: sticker.color,
  boardPosition: sticker.position,
  isVisible: sticker.isVisible,
  isRequired: sticker.isRequired,
  catalogIsRequired: sticker.catalogIsRequired,
  isSystem: sticker.isSystem,
  systemKey: sticker.systemKey,
  canEdit: sticker.canEdit,
  canManageRequired: sticker.canManageRequired,
  options: sticker.options ?? [],
})

export const reorderBoardStickerRows = (
  rows: BoardStickerRow[],
  activeFieldSettingId: number,
  oldIndex: number,
  newIndex: number,
): { reorderedRows: BoardStickerRow[]; position: number } | null => {
  if (oldIndex === newIndex) return null
  if (oldIndex < 0 || newIndex < 0 || oldIndex >= rows.length) return null

  const items = sortBoardStickerRows(rows)

  if (items[oldIndex]?.fieldSettingId !== activeFieldSettingId) return null

  const reorderedRows = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
    ...item,
    boardPosition: index + 1,
  }))

  const finalIndex = reorderedRows.findIndex(
    (item) => item.fieldSettingId === activeFieldSettingId,
  )
  if (finalIndex === -1) return null

  return {
    reorderedRows,
    position: finalIndex + 1,
  }
}

export const reorderTaskTrackerFilters = (
  filters: TaskTrackerFilter[],
  activeFilterId: number,
  oldIndex: number,
  newIndex: number,
): { reorderedFilters: TaskTrackerFilter[]; position: number } | null => {
  if (oldIndex === newIndex) return null
  if (oldIndex < 0 || newIndex < 0 || oldIndex >= filters.length) return null

  const items = sortTaskTrackerFilters(filters)

  if (items[oldIndex]?.id !== activeFilterId) return null

  const reorderedFilters = arrayMove(items, oldIndex, newIndex).map(
    (item, index) => ({
      ...item,
      position: index + 1,
    }),
  )

  const finalIndex = reorderedFilters.findIndex(
    (item) => item.id === activeFilterId,
  )
  if (finalIndex === -1) return null

  return {
    reorderedFilters,
    position: finalIndex + 1,
  }
}

const TASK_TRACKER_SYSTEM_BOARD_STICKER_FIELD_TYPES = new Set([
  'performer',
  'deadline',
  'task_created_at',
  'task_completed_at',
])

export const isTaskTrackerBoardStickerCatalogEditable = (
  row: Pick<BoardStickerRow, 'fieldType' | 'isSystem' | 'canEdit'>,
) => {
  if (!row.canEdit) return false

  if (row.isSystem) return false

  if (TASK_TRACKER_SYSTEM_BOARD_STICKER_FIELD_TYPES.has(row.fieldType)) {
    return false
  }

  if (isTaskTrackerSystemStickerFieldType(row.fieldType)) {
    return false
  }

  return true
}

export const mapBoardStickerRowToTaskTrackerFilter = (
  row: BoardStickerRow,
): TaskTrackerFilter => ({
  id: row.fieldSettingId,
  fieldType: row.fieldType,
  title: row.title,
  color: row.color,
  position: row.boardPosition,
  options: row.options,
  isRequired: row.catalogIsRequired,
  isSystem: row.isSystem,
  systemKey: row.systemKey,
})
